import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Spinner, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { getMyMeetingsApi } from "../../services/api";
import MetaTags from "../MetaTags";
import DataTable from "../DataTable";
import MeetingsKpiGrid from "./meetings/MeetingsKpiGrid";
import { getMeetingColumns } from "./meetings/MeetingColumns";
import MeetingMobileCard from "./meetings/MeetingMobileCard";
import "./MeetingsPage.css";

const parseLocalDateTime = (dateStr) => {
  if (!dateStr) return new Date();
  const cleanStr = String(dateStr).replace("Z", "").replace("T", " ");
  return new Date(cleanStr);
};

const MeetingsPage = () => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleCode, setRoleCode] = useState("");
  const navigate = useNavigate();

  const loadMeetings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyMeetingsApi();
      const allMeetings = res.data.data || [];
      const now = new Date();

      const activeMeetings = allMeetings.filter((meeting) => {
        const startDate = parseLocalDateTime(meeting.meeting_start || meeting.slot_datetime);
        const endDate = meeting.meeting_end 
          ? parseLocalDateTime(meeting.meeting_end) 
          : new Date(startDate.getTime() + 60 * 60 * 1000);
        const statusVal = String(meeting.meeting_status || meeting.payment_status || "BOOKED").toUpperCase();

        if (statusVal === "EXPIRED" || statusVal === "COMPLETED" || statusVal === "CANCELLED") {
          return false;
        }
        return endDate.getTime() >= now.getTime();
      });

      const mapped = activeMeetings.map((m) => {
        const startDate = parseLocalDateTime(m.meeting_start || m.slot_datetime);
        const endDate = parseLocalDateTime(m.meeting_end || m.slot_datetime);
        const statusVal = String(m.meeting_status || m.payment_status || "BOOKED").toUpperCase();

        return {
          id: m.id,
          projectName: m.role?.project?.project_name || "N/A",
          roleName: m.role?.role_name || "N/A",
          applicationId: m.application_id,
          bookingType: m.booking_type || "ONLINE_AUDITION",
          formattedDate: startDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
          formattedTime: `${startDate.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })} - ${endDate.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}`,
          statusVal,
          rawMeeting: m,
        };
      });

      setMeetings(mapped);
      setRoleCode(String(res.data.roleCode || ""));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMeetings();
  }, [loadMeetings]);

  const stats = useMemo(() => {
    const total = meetings.length;
    const booked = meetings.filter(m => m.statusVal === 'BOOKED').length;
    const expired = meetings.filter(m => m.statusVal === 'EXPIRED').length;
    return { total, booked, expired };
  }, [meetings]);

  const handleView = useCallback((meeting) => {
    const m = meeting.rawMeeting;
    if (roleCode === "300") {
      const roleId = m.role?.id;
      const slug = m.role?.urlSlug || m.role?.role_name?.toLowerCase()?.replace(/[–—]/g, "-")?.replace(/\s+/g, "-");
      navigate(`/audition/${roleId}/${slug}`);
      return;
    }
    if (roleCode === "200" || roleCode === "400") {
      navigate(`/dashboard/manage-applications/${m.joget_audition_id}`);
      return;
    }
  }, [roleCode, navigate]);

  const handleJoin = useCallback(async (meeting) => {
    if (meeting.statusVal === "EXPIRED") {
      Swal.fire({
        icon: "error",
        title: "Meeting Expired",
        text: "This meeting has already expired.",
        background: '#0E1124', color: '#fff', confirmButtonColor: '#d90429'
      });
      return;
    }

    const result = await Swal.fire({
      title: "Join Meeting?",
      text: `Application ID: #${meeting.applicationId}`,
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Yes, Join Now",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#dc3545",
      background: '#0E1124', color: '#fff'
    });

    if (result.isConfirmed) {
      window.open(`/meeting/audition-${meeting.applicationId}`, "_blank");
    }
  }, []);

  const columns = useMemo(() => getMeetingColumns({ handleView, handleJoin }), [handleView, handleJoin]);
  const renderMobileCard = useCallback((item) => (
    <MeetingMobileCard meeting={item} handleView={handleView} handleJoin={handleJoin} />
  ), [handleView, handleJoin]);

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="danger" />
        <p className="text-white mt-2">Loading Meetings...</p>
      </div>
    );
  }

  return (
    <div className="meetings-page-container p-2 p-md-3 animate-fade-in">
      <MetaTags title="My Meetings - Dashboard" />

      <div className="page-header mb-3">
        <h5 className="text-uppercase fw-bold text-white m-0">
          My <span style={{ color: 'var(--primary-color)' }}>Meetings</span>
        </h5>
        <small className="text-muted">Manage your audition meetings and online calls</small>
      </div>

      <MeetingsKpiGrid stats={stats} />

      <DataTable
        columns={columns}
        data={meetings}
        renderMobileItem={renderMobileCard}
        searchPlaceholder="Search project, role, application ID, or type..."
      />
    </div>
  );
};

export default MeetingsPage;